import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getAdminProfile } from '../../api/getAdminProfile';
import {
  Button,
  Menu,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useMediaQuery,
  ListItemButton,
} from '@mui/material';
import {
  Add as AddIcon,
  Store as StoreIcon,
  Warehouse as WarehouseIcon,
  Inventory as InventoryIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../auth/useAuth';

const Navbar: React.FC = () => {
  const [adminData, setAdminData] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const navigate = useNavigate();
  const { user, token } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        if (!token) return;
        const data = await getAdminProfile(user?.id);

        setAdminData(data);
      } catch (err) {
        console.error('Failed to fetch admin profile:', err);
      }
    };

    if (token) fetchProfile();
  }, [token, user]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (value: string) => {
    handleMenuClose();
    switch (value) {
      case 'create-store':
        navigate('/create-store');
        break;
      case 'create-warehouse':
        navigate('/create-warehouse');
        break;
      case 'create-inventory':
        navigate('/create-inventory');
        break;
      default:
        break;
    }
  };

  const menuItems = [
    {
      label: 'Create Store',
      value: 'create-store',
      icon: <StoreIcon fontSize="small" />,
    },
    {
      label: 'Create Warehouse',
      value: 'create-warehouse',
      icon: <WarehouseIcon fontSize="small" />,
    },
    {
      label: 'Create Inventory',
      value: 'create-inventory',
      icon: <InventoryIcon fontSize="small" />,
    },
  ];

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleLogin = () => navigate('/login');
  const handleRegister = () => navigate('/register');

  return (
    <nav style={styles.navbar}>
      <div style={styles.navContent}>
        {/* Brand */}
        <Link to="/" style={styles.brand}>
          QMagacin
        </Link>

        {/* Desktop Links */}
        {!isMobile && (
          <ul style={styles.navList}>
            <li>
              <Link to="/" style={styles.navLink}>
                Home
              </Link>
            </li>
            {token && (
              <>
                <li>
                  <Link to="/stores" style={styles.navLink}>
                    Stores
                  </Link>
                </li>
                <li>
                  <Link to="/warehouses" style={styles.navLink}>
                    Warehouses
                  </Link>
                </li>
              </>
            )}
            <li>
              <Link to="/dashboard" style={styles.navLink}>
                Dashboard
              </Link>
            </li>
          </ul>
        )}

        {/* Right Side */}
        <div style={styles.right}>
          {!isMobile ? (
            // Desktop buttons
            <>
              {token ? (
                <>
                  <Button
                    onClick={handleMenuOpen}
                    startIcon={<AddIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    sx={{
                      color: 'white',
                      textTransform: 'none',
                      fontSize: '16px',
                      padding: '8px 16px',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      },
                    }}
                  >
                    Create
                  </Button>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    {menuItems.map(item => (
                      <MenuItem
                        key={item.value}
                        onClick={() => handleMenuItemClick(item.value)}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'center',
                          }}
                        >
                          {item.icon}
                          <Typography>{item.label}</Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Menu>
                  <Link to="/profile" style={styles.adminName}>
                    {adminData?.name} {adminData?.surname}
                  </Link>
                  <button style={styles.button} onClick={handleLogout}>
                    Logout
                  </button>
                </>
              ) : (
                <>
                  <button style={styles.button} onClick={handleLogin}>
                    Login
                  </button>
                  <button style={styles.button} onClick={handleRegister}>
                    Register
                  </button>
                </>
              )}
            </>
          ) : (
            // Mobile hamburger
            <>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                sx={{ color: 'white' }}
              >
                <MenuIcon />
              </IconButton>
              <Drawer
                anchor="right"
                open={drawerOpen}
                onClose={() => setDrawerOpen(false)}
              >
                <Box sx={{ width: 250, p: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography variant="h6">Menu</Typography>
                    <IconButton onClick={() => setDrawerOpen(false)}>
                      <CloseIcon />
                    </IconButton>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <List>
                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/"
                        onClick={() => setDrawerOpen(false)}
                      >
                        <ListItemText primary="Home" />
                      </ListItemButton>
                    </ListItem>

                    {token && (
                      <>
                        <ListItem disablePadding>
                          <ListItemButton
                            component={Link}
                            to="/stores"
                            onClick={() => setDrawerOpen(false)}
                          >
                            <ListItemText primary="Stores" />
                          </ListItemButton>
                        </ListItem>

                        <ListItem disablePadding>
                          <ListItemButton
                            component={Link}
                            to="/warehouses"
                            onClick={() => setDrawerOpen(false)}
                          >
                            <ListItemText primary="Warehouses" />
                          </ListItemButton>
                        </ListItem>
                      </>
                    )}

                    <ListItem disablePadding>
                      <ListItemButton
                        component={Link}
                        to="/dashboard"
                        onClick={() => setDrawerOpen(false)}
                      >
                        <ListItemText primary="Dashboard" />
                      </ListItemButton>
                    </ListItem>
                  </List>
                  <Divider sx={{ my: 2 }} />
                  {token ? (
                    <>
                      <Typography variant="subtitle1" sx={{ mb: 1 }}>
                        {adminData?.name} {adminData?.surname}
                      </Typography>

                      {menuItems.map(item => (
                        <ListItem disablePadding key={item.value}>
                          <ListItemButton
                            onClick={() => {
                              handleMenuItemClick(item.value);
                              setDrawerOpen(false);
                            }}
                          >
                            <ListItemText primary={item.label} />
                          </ListItemButton>
                        </ListItem>
                      ))}

                      <ListItem disablePadding>
                        <ListItemButton onClick={handleLogout}>
                          <ListItemText primary="Logout" />
                        </ListItemButton>
                      </ListItem>
                    </>
                  ) : (
                    <>
                      <ListItem disablePadding>
                        <ListItemButton onClick={handleLogin}>
                          <ListItemText primary="Login" />
                        </ListItemButton>
                      </ListItem>
                      <ListItem disablePadding>
                        <ListItemButton onClick={handleRegister}>
                          <ListItemText primary="Register" />
                        </ListItemButton>
                      </ListItem>
                    </>
                  )}
                </Box>
              </Drawer>
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

const styles = {
  navbar: {
    backgroundColor: '#1a73e8',
    padding: '1rem 2rem',
    position: 'sticky' as const,
    top: 0,
    zIndex: 1000,
    color: '#fff',
  },
  navContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  brand: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#fff',
    textDecoration: 'none',
  },
  navList: {
    display: 'flex',
    listStyle: 'none',
    gap: '1.5rem',
    margin: 0,
    padding: 0,
  },
  navLink: {
    color: '#fff',
    textDecoration: 'none',
    fontSize: '1rem',
    fontWeight: 500,
  },
  right: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
  },
  adminName: {
    fontSize: '1rem',
    fontWeight: 500,
    color: '#e0f0ff',
    textDecoration: 'none',
  } as const,
  button: {
    padding: '0.5rem 1rem',
    fontSize: '0.95rem',
    backgroundColor: '#ffffff',
    color: '#1a73e8',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontWeight: 600,
  },
} as const;

export default Navbar;
